import {
  formatReceiptDate,
  formatReceiptTime
} from '../services/thermalReceipt/thermalReceiptFormatter';
import { getReceiptFormatLocale } from './receiptFormat';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function parseInstant(value) {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? new Date(value) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Persist real completion instant on saved sales (local date + time fields).
 */
export function stampSaleCompletionFields(saleData = {}) {
  const when =
    parseInstant(saleData.createdAt) ||
    parseInstant(saleData.completedAt) ||
    parseInstant(saleData.timestamp) ||
    new Date();

  const iso = when.toISOString();
  const dateOnly = `${when.getFullYear()}-${pad2(when.getMonth() + 1)}-${pad2(when.getDate())}`;
  const timeOnly = `${pad2(when.getHours())}:${pad2(when.getMinutes())}:${pad2(when.getSeconds())}`;

  return {
    ...saleData,
    createdAt: saleData.createdAt || iso,
    completedAt: saleData.completedAt || iso,
    timestamp: saleData.timestamp ?? when.getTime(),
    date: dateOnly,
    time: saleData.time || timeOnly
  };
}

function shouldUseLiveReceiptTime(saleData, options = {}) {
  if (options.useLiveTime === true) return true;
  if (options.useLiveTime === false) return false;
  return (
    saleData?.status === 'pending' &&
    !saleData?.createdAt &&
    !saleData?.completedAt &&
    saleData?.timestamp == null
  );
}

/**
 * Resolve sale instant for receipts and Ventes list.
 * Prefers stored completion fields — never applies live clock to completed sales.
 */
export function resolveSaleReceiptDate(saleData = {}, options = {}) {
  if (saleData?.date instanceof Date && !Number.isNaN(saleData.date.getTime())) {
    return saleData.date;
  }

  const storedInstant =
    parseInstant(saleData?.createdAt) ||
    parseInstant(saleData?.completedAt) ||
    parseInstant(saleData?.timestamp);

  if (storedInstant) {
    return storedInstant;
  }

  if (
    saleData?.time &&
    saleData?.date &&
    typeof saleData.date === 'string' &&
    !saleData.date.includes('T')
  ) {
    const combined = new Date(`${saleData.date}T${saleData.time}`);
    if (!Number.isNaN(combined.getTime())) {
      return combined;
    }
  }

  const rawDate = saleData?.date;
  if (!rawDate) {
    return new Date();
  }

  if (typeof rawDate === 'number') {
    const parsed = new Date(rawDate);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  if (typeof rawDate === 'string') {
    const parsedDate = new Date(rawDate);
    if (Number.isNaN(parsedDate.getTime())) {
      return new Date();
    }

    const isDateOnly =
      !rawDate.includes('T') &&
      !rawDate.includes(':') &&
      parsedDate.getHours() === 0 &&
      parsedDate.getMinutes() === 0 &&
      parsedDate.getSeconds() === 0;

    if (isDateOnly) {
      if (shouldUseLiveReceiptTime(saleData, options)) {
        const now = new Date();
        return new Date(
          parsedDate.getFullYear(),
          parsedDate.getMonth(),
          parsedDate.getDate(),
          now.getHours(),
          now.getMinutes(),
          now.getSeconds()
        );
      }
      return new Date(
        parsedDate.getFullYear(),
        parsedDate.getMonth(),
        parsedDate.getDate(),
        0,
        0,
        0
      );
    }

    return parsedDate;
  }

  return new Date();
}

export function formatSaleReceiptDate(sale, language = 'fr', options = {}) {
  return formatReceiptDate(
    resolveSaleReceiptDate(sale, options),
    getReceiptFormatLocale(language)
  );
}

export function formatSaleReceiptTime(sale, language = 'fr', options = {}) {
  return formatReceiptTime(
    resolveSaleReceiptDate(sale, options),
    getReceiptFormatLocale(language)
  );
}
